import { defineComponent as _defineComponent } from 'vue'
import { computed, nextTick, PropType, ref } from 'vue';
import { ChecklistDropdownOption } from '@/interfaces/components/configurator/ChecklistDropdownOption';


export default /*#__PURE__*/_defineComponent({
  __name: 'CustomSelect',
  props: {
    id: { type: String, required: true },
    value: { type: String, default: '' },
    options: { type: Array as PropType<ChecklistDropdownOption[]>, default: () => [] },
    disabled: { type: Boolean, default: false },
    tabIndex: { type: Number, default: 0 },
},
  emits: ['blur', 'change', 'focus'],
  setup(__props, { emit }) {

const props = __props;





const NO_ACTIVE_OPTION_INDEX = -1;
const FIRST_OPTION_INDEX = 0;

const trigger = ref<HTMLInputElement | null>(null);
const dropdown = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const searchQuery = ref('');
const activeOptionIndex = ref(NO_ACTIVE_OPTION_INDEX);
const dropdownStyle = ref({ top: '0', left: '0', minWidth: '0' });

const selectedOption = computed(() => props.options.find((option) => option.vId === props.value));

const filteredOptions = computed(() => {
    const normalizedSearch = searchQuery.value.trim().toLocaleLowerCase();
    if (!normalizedSearch) {
        return props.options;
    }

    return props.options.filter((option) => option.value.toLocaleLowerCase().includes(normalizedSearch));
});

const activeOptionId = computed(() => {
    if (!isOpen.value || activeOptionIndex.value === NO_ACTIVE_OPTION_INDEX) {
        return undefined;
    }

    return `${props.id}-option-${activeOptionIndex.value}`;
});

async function open() {
    if (props.disabled || isOpen.value) {
        return;
    }

    searchQuery.value = '';
    const selectedOptionIndex = props.options.findIndex((option) => option.vId === props.value);

    if (selectedOptionIndex !== NO_ACTIVE_OPTION_INDEX) {
        activeOptionIndex.value = selectedOptionIndex;
    } else if (props.options.length > 0) {
        activeOptionIndex.value = FIRST_OPTION_INDEX;
    } else {
        activeOptionIndex.value = NO_ACTIVE_OPTION_INDEX;
    }

    isOpen.value = true;
    setStickyRowDropdownState(true);
    await nextTick();
    positionDropdown();
    scrollToActiveOption();
}

function close() {
    isOpen.value = false;
    setStickyRowDropdownState(false);
    searchQuery.value = '';
}

function setStickyRowDropdownState(isDropdownOpen: boolean) {
    const stickyRow = trigger.value?.closest('.c-checklist__row.is-multiposition');
    stickyRow?.classList.toggle('has-open-custom-select', isDropdownOpen);
}

function onArrowClick(event: MouseEvent) {
    event.preventDefault();
    event.stopPropagation();

    if (props.disabled) {
        return;
    }

    trigger.value?.focus();

    if (isOpen.value) {
        close();
        return;
    }

    open();
}

function selectOption(selectedValue: string) {
    emit('change', selectedValue);
    close();
    nextTick(() => trigger.value?.focus());
}

function onSearch(event: Event) {
    searchQuery.value = (event.target as HTMLInputElement).value;
    activateFirstFilteredOption();
    nextTick(scrollToActiveOption);
}

async function onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowDown') {
        event.preventDefault();
        event.stopPropagation();

        if (!isOpen.value) {
            await open();
            return;
        }

        moveToNextOption();
        return;
    }

    if (event.key === 'ArrowUp') {
        event.preventDefault();
        event.stopPropagation();

        if (!isOpen.value) {
            await open();
            return;
        }

        moveToPreviousOption();
        return;
    }

    if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();

        if (!isOpen.value) {
            await open();
        } else if (activeOptionIndex.value !== NO_ACTIVE_OPTION_INDEX) {
            const activeOption = filteredOptions.value[activeOptionIndex.value];
            if (activeOption) {
                selectOption(activeOption.vId);
            }
        }
        return;
    }

    if (event.key === 'Escape' && isOpen.value) {
        event.preventDefault();
        event.stopPropagation();
        close();
        return;
    }

    if (event.code === 'Space' && !isOpen.value) {
        event.preventDefault();
        event.stopPropagation();
        await open();
        return;
    }

    if (event.key === 'Tab') {
        close();
        return;
    }

    if (!isOpen.value && isSearchCharacter(event)) {
        event.preventDefault();
        event.stopPropagation();
        await open();
        searchQuery.value = event.key;
        activateFirstFilteredOption();
        nextTick(scrollToActiveOption);
    }
}

function isSearchCharacter(event: KeyboardEvent) {
    const isSingleCharacter = event.key.length === 1;
    const hasKeyboardModifier = event.altKey || event.ctrlKey || event.metaKey;

    return isSingleCharacter && !hasKeyboardModifier;
}

function activateFirstFilteredOption() {
    if (filteredOptions.value.length === 0) {
        activeOptionIndex.value = NO_ACTIVE_OPTION_INDEX;
        return;
    }

    activeOptionIndex.value = FIRST_OPTION_INDEX;
}

function moveToNextOption() {
    const optionCount = filteredOptions.value.length;
    if (optionCount === 0) {
        return;
    }

    const lastOptionIndex = optionCount - 1;
    if (activeOptionIndex.value >= lastOptionIndex) {
        activeOptionIndex.value = FIRST_OPTION_INDEX;
    } else {
        activeOptionIndex.value += 1;
    }

    nextTick(scrollToActiveOption);
}

function moveToPreviousOption() {
    const optionCount = filteredOptions.value.length;
    if (optionCount === 0) {
        return;
    }

    const lastOptionIndex = optionCount - 1;
    if (activeOptionIndex.value <= FIRST_OPTION_INDEX) {
        activeOptionIndex.value = lastOptionIndex;
    } else {
        activeOptionIndex.value -= 1;
    }

    nextTick(scrollToActiveOption);
}

function scrollToActiveOption() {
    dropdown.value?.querySelector('.custom-select__option--active')?.scrollIntoView({ block: 'nearest' });
}

function positionDropdown() {
    if (!trigger.value || !dropdown.value) {
        return;
    }

    const triggerRect = trigger.value.getBoundingClientRect();
    const dropdownRect = dropdown.value.getBoundingClientRect();
    const availableBelow = window.innerHeight - triggerRect.bottom;
    const dropdownWidth = Math.max(dropdownRect.width, triggerRect.width);
    const top =
        availableBelow >= dropdownRect.height || availableBelow >= triggerRect.top
            ? triggerRect.bottom + 4
            : triggerRect.top - dropdownRect.height - 4;
    const left = Math.max(4, Math.min(triggerRect.left, window.innerWidth - dropdownWidth - 4));

    dropdownStyle.value = {
        top: `${top}px`,
        left: `${left}px`,
        minWidth: `${triggerRect.width}px`,
    };
}

function onFocusOut(event: FocusEvent) {
    if ((event.currentTarget as HTMLElement).contains(event.relatedTarget as Node | null)) {
        return;
    }

    close();
    emit('blur', event);
}

return { __sfc: true,props, emit, NO_ACTIVE_OPTION_INDEX, FIRST_OPTION_INDEX, trigger, dropdown, isOpen, searchQuery, activeOptionIndex, dropdownStyle, selectedOption, filteredOptions, activeOptionId, open, close, setStickyRowDropdownState, onArrowClick, selectOption, onSearch, onKeyDown, isSearchCharacter, activateFirstFilteredOption, moveToNextOption, moveToPreviousOption, scrollToActiveOption, positionDropdown, onFocusOut }
}

})